import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useSearchParams, useNavigate, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { useDestrierTheme, getCardSx } from '../ThemeContext';
import { useDataCache } from '../DataCacheContext';
import { API_BASE } from '../api';

const ACCENT = '#8F7BE0';
const BLUE = '#00A5C4';
const GOLD = '#DE7345';
// No green — use BLUE for positive/completed states per Destrier palette
const RED = '#E4446E';

const ARCHETYPE_IMAGES = {
  snake: '/Snake.png', blackpanther: '/BlackPanther.png', fox: '/Fox.png', dragon: '/Dragon.png',
  octopus: '/Octopus.png', elephant: '/Elephant.png', phoenix: '/Phoenix.png', stag: '/Stag.png',
  shade: '/Snake.png', reaper: '/BlackPanther.png', demon: '/Fox.png', inferno: '/Dragon.png',
  leviathan: '/Octopus.png', monolith: '/Elephant.png', guardian: '/Stag.png',
};

const SINGULAR_NAMES = {
  Snakes: 'Snake', Panthers: 'Panther', Foxes: 'Fox', Dragons: 'Dragon',
  Octopuses: 'Octopus', Elephants: 'Elephant', Phoenixes: 'Phoenix', Stags: 'Stag',
};

const TYPE_COLORS = { bullet: '#E4446E', blitz: '#DE7345', rapid: '#00A5C4', classical: '#8F7BE0', daily: '#D66DD3' };

function ChartTooltip({ active, payload, c }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <Box sx={{ background: c.bg, border: `1px solid ${c.textMuted}25`, borderRadius: '12px', p: 2, boxShadow: `0 4px 16px ${c.textMuted}15` }}>
      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mb: 0.5 }}>{d.date}</Typography>
      <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 700 }}>{d.elo} Elo</Typography>
    </Box>
  );
}

/* ── Insight card — reusable for all 6 cards ── */
function InsightCard({ card, c, navigateTo }) {
  return (
    <Box sx={{ ...getCardSx(c), p: 3, border: `1.5px solid ${card.color}20`, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '10px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: `${card.color}15`, color: card.color,
        }}>
          {card.icon}
        </Box>
        <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {card.tag}
        </Typography>
      </Box>
      <Typography sx={{ color: c.textDark, fontSize: '1.2rem', fontWeight: 700, mb: 0.5 }}>
        {card.headline}
      </Typography>
      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', lineHeight: 1.6, flex: 1 }}>
        {card.detail}
      </Typography>
      <Box
        onClick={() => navigateTo(card.path)}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, cursor: 'pointer', width: 'fit-content', '&:hover': { opacity: 0.8 } }}
      >
        <Typography sx={{ color: card.color, fontSize: '1.05rem', fontWeight: 600 }}>
          {card.cta}
        </Typography>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={card.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
        </svg>
      </Box>
    </Box>
  );
}

/* ── Animated counter ── */
function AnimNum({ value, prefix = '', suffix = '', duration = 1200 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef();
  useEffect(() => {
    let start = null;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (ts) => {
      if (!start) start = ts;
      const t = Math.min((ts - start) / duration, 1);
      setDisplay(Math.round(ease(t) * value));
      if (t < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(ref.current);
  }, [value, duration]);
  return <>{prefix}{display}{suffix}</>;
}

/* ── Streak fire icon ── */
function FireIcon({ size = 24, active }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={active ? GOLD : '#555'} opacity={active ? 1 : 0.3}>
      <path d="M12 23c-4.97 0-8-3.03-8-7 0-2.45 1.4-5.24 2.8-7.2.36-.5 1.12-.38 1.3.2.42 1.36.87 2.14 1.4 2.8.16-.7.56-1.68 1.22-2.88C12.06 6.22 13 3.5 13 1c0-.55.47-.99 1-.91 2.48.36 5.7 3.27 6.72 6.51C21.52 9.12 22 11.3 22 13c0 5.5-4.5 10-10 10z" />
    </svg>
  );
}

/* ── Drill type icon ── */
function DrillIcon({ type, size = 20 }) {
  const props = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (type) {
    case 'punishment':
      return <svg {...props}><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="M15 9l-6 6M9 9l6 6"/></svg>;
    case 'opening':
      return <svg {...props}><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>;
    case 'checkmate':
      return <svg {...props}><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/><circle cx="12" cy="12" r="4"/></svg>;
    case 'blunder':
      return <svg {...props}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
    default:
      return <svg {...props}><rect x="3" y="3" width="18" height="18" rx="2"/></svg>;
  }
}

/* ── Insight cards config — each has a headline + sentence, like Biggest Edge/Leak ── */
const INSIGHT_CARDS = [
  {
    key: 'openings',
    tag: 'Opening Repertoire',
    path: '/dashboard/openings',
    color: BLUE,
    cta: 'View all openings',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>,
    getHeadline: (d) => d?.top_white_opening && d?.top_black_opening ? `${d.top_white_opening} / ${d.top_black_opening}` : d?.top_white_opening || 'Your openings',
    getDetail: (d) => {
      const parts = [];
      if (d?.unique_openings) parts.push(`${d.unique_openings} openings in your repertoire`);
      if (d?.worst_opening && d?.worst_opening_wr != null) parts.push(`${d.worst_opening} is your weakest at ${Math.round(d.worst_opening_wr * 100)}% win rate — worth reviewing`);
      return parts.join('. ') + '.' || 'Explore your opening choices and find where to gain Elo.';
    },
  },
  {
    key: 'habits',
    tag: 'Your Habits',
    path: '/dashboard/habits',
    color: GOLD,
    cta: 'View habits report',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    getHeadline: (d) => d?.favorite_piece ? `${d.favorite_piece} player` : 'Your playing patterns',
    getDetail: (d) => {
      const parts = [];
      if (d?.best_time?.day && d?.best_time?.hour != null) parts.push(`You perform best on ${d.best_time.day}s around ${d.best_time.hour}:00`);
      if (d?.teasers?.time_trouble_rate != null) {
        const tr = Math.round(d.teasers.time_trouble_rate * 100);
        parts.push(tr > 30 ? `${tr}% of your games end in time trouble — this is costing you Elo` : `${tr}% time trouble rate — solid clock management`);
      }
      return parts.join('. ') + '.' || 'Discover your time patterns and piece preferences.';
    },
  },
  {
    key: 'rivals',
    tag: 'Opponent Breakdown',
    path: '/dashboard/rivals',
    color: RED,
    cta: 'View all rivals',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
    getHeadline: (d) => {
      if (d?.biggest_upset) return `Biggest upset: +${d.biggest_upset} Elo`;
      if (d?.win_rate != null) return `${Math.round(d.win_rate * 100)}% win rate`;
      return 'Your opponents';
    },
    getDetail: (d) => {
      if (d?.white_win_rate != null && d?.black_win_rate != null) {
        const ww = Math.round(d.white_win_rate * 100), bw = Math.round(d.black_win_rate * 100);
        const stronger = ww > bw ? 'White' : 'Black';
        return `You win ${ww}% as White and ${bw}% as Black — ${stronger} is your stronger side. See who you dominate and who gives you trouble.`;
      }
      return 'Analyze your performance against different rating brackets and find your sweet spot.';
    },
  },
  {
    key: 'training',
    tag: 'Practice & Improve',
    path: '/dashboard/training',
    color: ACCENT,
    cta: 'Start training',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="12" y1="3" x2="12" y2="21"/><circle cx="8" cy="8" r="1.5" fill="currentColor" stroke="none"/><circle cx="16" cy="16" r="1.5" fill="currentColor" stroke="none"/></svg>,
    getHeadline: (d) => d?.all_weaknesses?.length ? `${d.all_weaknesses.length} areas to drill` : '7 training modes',
    getDetail: (d) => {
      const parts = ['Practice openings, find missed checkmates, review blunders, and punish opponent mistakes'];
      if (d?.worst_opening) parts.push(`Start with ${d.worst_opening} — your weakest opening`);
      return parts.join('. ') + '.';
    },
  },
];

export default function HomePage() {
  const { c } = useDestrierTheme();
  const cache = useDataCache();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  const username = searchParams.get('username');
  const displayName = outletContext.profile?.display_name || username;

  const [drills, setDrills] = useState(null);
  const [weeklyProgress, setWeeklyProgress] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);
  const [selectedType, setSelectedType] = useState(null); // null = auto-select main type

  // Fetch all data in parallel, using cache when available
  useEffect(() => {
    if (!username) return;

    const drillsCk = `daily-drills:${username}`;
    const weeklyCk = `weekly-progress:${username}`;
    const reportCk = `free-report:${username}:all`;

    const cachedDrills = cache.get(drillsCk);
    const cachedWeekly = cache.get(weeklyCk);
    const cachedReport = cache.get(reportCk);

    // If everything is cached, use it immediately — no loading spinner
    if (cachedDrills && cachedWeekly && cachedReport) {
      setDrills(cachedDrills);
      setWeeklyProgress(cachedWeekly);
      setReportData(cachedReport);
      setLoading(false);
      return;
    }

    // Otherwise show loading and fetch missing data
    setLoading(true);

    const fetchAll = async () => {
      const promises = [];
      const keys = [];

      if (!cachedDrills) {
        keys.push('drills');
        promises.push(fetch(`${API_BASE}/daily-drills?username=${encodeURIComponent(username)}`).then(r => r.json()).catch(() => null));
      }
      if (!cachedWeekly) {
        keys.push('weekly');
        promises.push(fetch(`${API_BASE}/weekly-progress?username=${encodeURIComponent(username)}`).then(r => r.json()).catch(() => null));
      }
      if (!cachedReport) {
        keys.push('report');
        promises.push(fetch(`${API_BASE}/free-report?username=${encodeURIComponent(username)}&period=all`).then(r => r.json()).catch(() => null));
      }

      const results = await Promise.all(promises);

      let i = 0;
      for (const key of keys) {
        const data = results[i++];
        if (key === 'drills' && data) { cache.set(drillsCk, data); setDrills(data); }
        else if (key === 'weekly' && data) { cache.set(weeklyCk, data); setWeeklyProgress(data); }
        else if (key === 'report' && data) { cache.set(reportCk, data); setReportData(data); }
      }

      // Apply any cached values that didn't need fetching
      if (cachedDrills) setDrills(cachedDrills);
      if (cachedWeekly) setWeeklyProgress(cachedWeekly);
      if (cachedReport) setReportData(cachedReport);

      setLoading(false);
    };

    fetchAll();
  }, [username]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCompleteDrill = useCallback(async (drillType) => { // eslint-disable-line no-unused-vars
    if (!username || completing) return;
    setCompleting(drillType);
    try {
      const resp = await fetch(`${API_BASE}/daily-drills/complete?username=${encodeURIComponent(username)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ drill_type: drillType }),
      });
      if (resp.ok) {
        // Refetch drills and update cache
        const updated = await fetch(`${API_BASE}/daily-drills?username=${encodeURIComponent(username)}`).then(r => r.json());
        cache.set(`daily-drills:${username}`, updated);
        setDrills(updated);
      }
    } catch (e) {
      console.error('Failed to complete drill:', e);
    } finally {
      setCompleting(null);
    }
  }, [username, completing, cache]);

  const navigateTo = useCallback((path) => {
    navigate(`${path}?username=${encodeURIComponent(username)}`);
  }, [navigate, username]);

  // Available time controls
  const availableTypes = useMemo(() => {
    const types = reportData?.elo_trajectory?.available_types;
    if (!types?.length) return [];
    return types.sort((a, b) => b.games - a.games);
  }, [reportData]);

  // Auto-select main type on first load
  const activeType = selectedType || reportData?.elo_trajectory?.main_type || null;

  // Elo chart data — last N games (up to 50) for the selected type
  const eloChart = useMemo(() => {
    const traj = reportData?.elo_trajectory;
    if (!traj?.types) return null;
    const typeData = traj.types[activeType];
    if (!typeData?.points?.length) return null;
    const pts = typeData.points;
    const count = Math.min(50, pts.length);
    const recent = pts.slice(-count);
    const net = recent.length >= 2 ? recent[recent.length - 1].elo - recent[0].elo : 0;
    return { data: recent, shown: count, type: activeType, current: typeData.current, peak: typeData.peak, net, games: typeData.games };
  }, [reportData, activeType]);

  // Per-type stats (win rate, games, etc.)
  const typeStats = useMemo(() => {
    const list = reportData?.stats_by_type;
    if (!list?.length || !activeType) return null;
    return list.find(s => s.game_type === activeType) || null;
  }, [reportData, activeType]);

  // Archetype data
  const archetype = reportData?.archetype;
  const archetypeImage = archetype ? ARCHETYPE_IMAGES[archetype.profile_key] : null;
  const archetypeName = archetype ? (SINGULAR_NAMES[archetype.profile_name] || archetype.profile_name) : null;

  // Derived drill state
  const streak = drills?.streak || {};
  const today = drills?.today || {};
  const drillList = drills?.drills || [];
  const allComplete = today.allComplete;
  const completedCount = today.drillsCompleted || 0;
  const totalDrills = today.drillsTotal || 5;
  const progressPct = Math.round((completedCount / totalDrills) * 100);

  // ── Loading state ──
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', background: c.pageBg || c.bg }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', gap: 3 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: '50%', border: `3px solid ${ACCENT}20`, borderTopColor: ACCENT, animation: 'spin 0.8s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
          <Typography sx={{ color: c.textMuted, fontSize: '1.1rem', fontWeight: 600 }}>
            Preparing your daily drills...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: c.pageBg || c.bg }}>
      <Box sx={{ pt: { xs: 9, md: 10 }, pb: 6, px: { xs: 2, md: 3, lg: 4 } }}>

        {/* ── Greeting ── */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ color: c.textDark, fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.6rem' }, fontWeight: 900, letterSpacing: '-0.03em' }}>
            Welcome back, {displayName}
          </Typography>
          <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, mt: 0.5, lineHeight: 1.7 }}>
            Here's your complete chess overview — your rating trends, strengths, weaknesses, and what to work on next. Every game you play feeds into your personal analysis so you can track exactly where you're improving.
          </Typography>
        </Box>

        {/* ══════════════════════════════════════════════════════
            TIME CONTROL SELECTOR
        ══════════════════════════════════════════════════════ */}
        {availableTypes.length > 1 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {availableTypes.map((t) => {
              const isActive = t.type === activeType;
              const color = TYPE_COLORS[t.type] || ACCENT;
              return (
                <Box
                  key={t.type}
                  onClick={() => setSelectedType(t.type)}
                  sx={{
                    px: 2,
                    py: 0.8,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    backgroundColor: isActive ? `${color}18` : 'transparent',
                    border: `1.5px solid ${isActive ? color : c.textMuted + '20'}`,
                    transition: 'all 0.15s ease',
                    '&:hover': { backgroundColor: `${color}10`, border: `1.5px solid ${color}60` },
                  }}
                >
                  <Typography sx={{
                    color: isActive ? color : c.textMuted,
                    fontSize: '1.05rem',
                    fontWeight: isActive ? 700 : 500,
                    textTransform: 'capitalize',
                  }}>
                    {t.type}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* ══════════════════════════════════════════════════════
            QUICK STATS + ARCHETYPE (side by side on desktop)
        ══════════════════════════════════════════════════════ */}
        {reportData && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 1.5,
              mb: 4,
            }}
          >
            {/* ── Stats 2x2 grid ── */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 1.5,
              flex: { lg: '1 1 0' },
              minWidth: 0,
            }}>
              {(() => {
                const elo = typeStats?.elo || eloChart?.current || reportData.current_elo;
                const wr = typeStats ? Math.round(typeStats.win_rate * 100) : (reportData.win_rate != null ? Math.round(reportData.win_rate * 100) : null);
                const net = eloChart?.net || 0;
                const shown = eloChart?.shown || 0;

                const eloDesc = elo >= 2000 ? 'Expert territory — top percentile'
                  : elo >= 1600 ? 'Strong club player level'
                  : elo >= 1200 ? 'Solid intermediate — room to push higher'
                  : 'Building your foundation — keep playing';

                const wrDesc = wr >= 60 ? 'Dominant — you\'re outplaying your bracket'
                  : wr >= 52 ? 'Positive edge — you\'re climbing steadily'
                  : wr >= 48 ? 'Balanced — small adjustments can tip the scale'
                  : 'Tough stretch — focus on your weaknesses below';

                const currentStreak = streak.currentStreak || 0;
                const streakDesc = currentStreak >= 7 ? 'Incredible consistency — keep the fire burning'
                  : currentStreak >= 3 ? 'Building momentum — don\'t break the chain'
                  : currentStreak >= 1 ? 'You\'re on it — come back tomorrow to keep going'
                  : 'Start a new streak by completing today\'s drills';

                const netDesc = net > 0
                  ? `You gained ${net} Elo over your last ${shown} games — your work is paying off`
                  : net < 0
                  ? `You dropped ${Math.abs(net)} Elo over your last ${shown} games — check your weaknesses below for where to recover`
                  : `No change over your last ${shown} games — steady play`;

                const statCards = [
                  { label: 'Current Elo', value: elo, color: c.textDark, desc: eloDesc },
                  { label: 'Win Rate', value: wr, suffix: '%', color: BLUE, desc: wrDesc },
                  { label: 'Recent Elo Change', value: net, color: net >= 0 ? BLUE : RED, prefix: net > 0 ? '+' : '', desc: netDesc },
                ];

                // Week day streak dots
                const dayOfWeek = (new Date().getDay() + 6) % 7; // Monday = 0
                const weekDays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

                return (
                  <>
                    {statCards.map((stat) => (
                      <Box key={stat.label} sx={{
                        ...getCardSx(c),
                        p: 2.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}>
                        <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          {stat.label}
                        </Typography>
                        <Typography sx={{ color: stat.color, fontSize: '2rem', fontWeight: 900, lineHeight: 1 }}>
                          {stat.value != null ? <AnimNum value={stat.value} prefix={stat.prefix || ''} suffix={stat.suffix || ''} /> : '—'}
                        </Typography>
                        <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', lineHeight: 1.5, mt: 0.5 }}>
                          {stat.desc}
                        </Typography>
                      </Box>
                    ))}

                    {/* Day Streak — special card */}
                    <Box sx={{
                      ...getCardSx(c),
                      p: 2.5,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      border: currentStreak > 0 ? `1.5px solid ${GOLD}20` : undefined,
                      position: 'relative',
                      overflow: 'hidden',
                    }}>
                      {currentStreak > 0 && (
                        <Box sx={{
                          position: 'absolute', top: -30, right: -30, width: 120, height: 120,
                          borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}12 0%, transparent 70%)`,
                          pointerEvents: 'none',
                        }} />
                      )}
                      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Day Streak
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FireIcon size={32} active={currentStreak > 0} />
                        <Typography sx={{ color: currentStreak > 0 ? GOLD : c.textMuted, fontSize: '2rem', fontWeight: 900, lineHeight: 1 }}>
                          <AnimNum value={currentStreak} />
                        </Typography>
                      </Box>
                      {/* Week progress dots */}
                      <Box sx={{ display: 'flex', gap: 0.8, mt: 1 }}>
                        {weekDays.map((day, i) => {
                          const isPast = i < dayOfWeek;
                          const isToday = i === dayOfWeek;
                          const isActive = isPast && currentStreak > (dayOfWeek - i);
                          const isTodayDone = isToday && allComplete;
                          return (
                            <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3, flex: 1 }}>
                              <Box sx={{
                                width: 28, height: 28, borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                backgroundColor: (isActive || isTodayDone) ? `${GOLD}20` : isToday ? `${ACCENT}12` : 'transparent',
                                border: isToday ? `2px solid ${ACCENT}` : (isActive || isTodayDone) ? `2px solid ${GOLD}50` : `1px solid ${c.textMuted}20`,
                              }}>
                                {(isActive || isTodayDone) ? (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                ) : (
                                  <Typography sx={{ color: isToday ? ACCENT : c.textMuted, fontSize: '0.75rem', fontWeight: 700, lineHeight: 1 }}>
                                    {day}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', lineHeight: 1.5, mt: 0.5 }}>
                        {streakDesc}
                      </Typography>
                    </Box>
                  </>
                );
              })()}
            </Box>

            {/* ── Archetype card (right side) ── */}
            {archetype && (
              <Box
                sx={{
                  ...getCardSx(c),
                  p: 3,
                  border: `1.5px solid ${ACCENT}20`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  overflow: 'hidden',
                  position: 'relative',
                  flex: { lg: '0 0 280px' },
                }}
              >
                {/* Subtle glow */}
                <Box sx={{
                  position: 'absolute', top: -60, right: -60, width: 240, height: 240,
                  borderRadius: '50%', background: `radial-gradient(circle, ${ACCENT}10 0%, transparent 70%)`,
                  pointerEvents: 'none',
                }} />

                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', position: 'relative' }}>
                  Your Play Style
                </Typography>

                {/* Animal image */}
                {archetypeImage && (
                  <Box sx={{
                    width: 100, height: 100,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    flexShrink: 0,
                    backgroundColor: `${ACCENT}10`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: `2px solid ${ACCENT}30`,
                  }}>
                    <img src={archetypeImage} alt={archetypeName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                )}

                <Box sx={{ textAlign: 'center', position: 'relative' }}>
                  <Typography sx={{ color: c.textDark, fontSize: '1.3rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
                    The {archetypeName}
                  </Typography>
                  {archetype.tagline && (
                    <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mt: 0.5, fontStyle: 'italic', lineHeight: 1.5 }}>
                      "{archetype.tagline}"
                    </Typography>
                  )}
                  {reportData?.gm_matches_top3?.[0] && (
                    <Typography sx={{ color: ACCENT, fontSize: '1.05rem', fontWeight: 600, mt: 1 }}>
                      Plays like {reportData.gm_matches_top3[0].name}
                    </Typography>
                  )}
                  <Box
                    onClick={() => navigateTo('/dashboard/archetype')}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 1.5, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                  >
                    <Typography sx={{ color: ACCENT, fontSize: '1.05rem', fontWeight: 600 }}>
                      Explore your style
                    </Typography>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                    </svg>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* ══════════════════════════════════════════════════════
            ELO PROGRESSION CHART
        ══════════════════════════════════════════════════════ */}
        {eloChart && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            sx={{
              ...getCardSx(c),
              p: { xs: 2.5, md: 3.5 },
              mb: 4,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography sx={{ color: c.textDark, fontSize: { xs: '1.3rem', md: '1.6rem' }, fontWeight: 800, letterSpacing: '-0.02em' }}>
                  Rating Progression
                </Typography>
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mt: 0.3 }}>
                  Last {eloChart.shown} game{eloChart.shown !== 1 ? 's' : ''}{eloChart.games ? ` of ${eloChart.games} total` : ''}
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                px: 1.5,
                py: 0.5,
                borderRadius: '10px',
                backgroundColor: `${eloChart.net >= 0 ? BLUE : RED}10`,
                border: `1px solid ${eloChart.net >= 0 ? BLUE : RED}20`,
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={eloChart.net >= 0 ? BLUE : RED} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  {eloChart.net >= 0
                    ? <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></>
                    : <><polyline points="23 18 13.5 8.5 8.5 13.5 1 6" /><polyline points="17 18 23 18 23 12" /></>
                  }
                </svg>
                <Typography sx={{ color: eloChart.net >= 0 ? BLUE : RED, fontSize: '1.15rem', fontWeight: 700 }}>
                  {eloChart.net > 0 ? '+' : ''}{eloChart.net}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ width: '100%', height: { xs: 200, md: 260 } }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={eloChart.data} margin={{ top: 5, right: 20, bottom: 10, left: 10 }}>
                  <defs>
                    <linearGradient id="homeEloGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={TYPE_COLORS[eloChart.type] || ACCENT} stopOpacity={0.15} />
                      <stop offset="100%" stopColor={TYPE_COLORS[eloChart.type] || ACCENT} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={`${c.textMuted}12`} strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fill: c.textMuted, fontSize: 14, fontWeight: 500 }} tickLine={false}
                    axisLine={{ stroke: `${c.textMuted}20` }}
                    tickFormatter={(d) => { const p = d.split('.'); return p.length >= 2 ? `${p[1]}/${p[0].slice(2)}` : d; }}
                    interval="preserveStartEnd" minTickGap={60} />
                  <YAxis tick={{ fill: c.textMuted, fontSize: 14, fontWeight: 500 }} tickLine={false}
                    axisLine={{ stroke: `${c.textMuted}20` }} domain={['auto', 'auto']} width={50} />
                  <Tooltip content={<ChartTooltip c={c} />} />
                  <Area type="monotone" dataKey="elo" stroke={TYPE_COLORS[eloChart.type] || ACCENT}
                    strokeWidth={2} fill="url(#homeEloGrad)" dot={false}
                    activeDot={{ r: 5, stroke: c.bg, strokeWidth: 2 }} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
            <Box
              onClick={() => navigateTo('/dashboard/progress')}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2, cursor: 'pointer', width: 'fit-content' }}
            >
              <Typography sx={{ color: TYPE_COLORS[eloChart.type] || ACCENT, fontSize: '1.05rem', fontWeight: 600 }}>
                View full rating history
              </Typography>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={TYPE_COLORS[eloChart.type] || ACCENT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
              </svg>
            </Box>
          </Box>
        )}

        {/* ══════════════════════════════════════════════════════
            TOP STRENGTH / TOP WEAKNESS
        ══════════════════════════════════════════════════════ */}
        {/* ══════════════════════════════════════════════════════
            INSIGHT CARDS — 3 per row, unified style
        ══════════════════════════════════════════════════════ */}
        {reportData && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 1.5,
              mb: 4,
            }}
          >
            {/* Biggest Edge */}
            {reportData.top_strength && (() => {
              const card = { tag: 'Biggest Edge', color: BLUE, path: '/dashboard/strengths', cta: 'View all strengths',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>,
                headline: reportData.top_strength.label, detail: reportData.top_strength.detail };
              return <InsightCard key="edge" card={card} c={c} navigateTo={navigateTo} />;
            })()}

            {/* Biggest Leak */}
            {reportData.top_weakness && (() => {
              const card = { tag: 'Biggest Leak', color: RED, path: '/dashboard/weaknesses', cta: 'View all weaknesses',
                icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
                headline: reportData.top_weakness.label, detail: reportData.top_weakness.detail };
              return <InsightCard key="leak" card={card} c={c} navigateTo={navigateTo} />;
            })()}

            {/* Dynamic insight cards */}
            {INSIGHT_CARDS.map((tile) => {
              const card = { ...tile, headline: tile.getHeadline(reportData), detail: tile.getDetail(reportData) };
              return <InsightCard key={tile.key} card={card} c={c} navigateTo={navigateTo} />;
            })}
          </Box>
        )}

        {/* ══════════════════════════════════════════════════════
            DAILY DRILLS
        ══════════════════════════════════════════════════════ */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            ...getCardSx(c),
            p: { xs: 2.5, md: 3.5 },
            mb: 4,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Subtle glow behind streak */}
          {streak.currentStreak > 0 && (
            <Box sx={{
              position: 'absolute',
              top: -40,
              right: -40,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${GOLD}15 0%, transparent 70%)`,
              pointerEvents: 'none',
            }} />
          )}

          {/* Header: Daily Drills + Streak */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography sx={{ color: c.textDark, fontSize: { xs: '1.3rem', md: '1.6rem' }, fontWeight: 800, letterSpacing: '-0.02em' }}>
                Daily Drills
              </Typography>
              <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mt: 0.3 }}>
                5 personalized challenges from your games
              </Typography>
            </Box>

            {/* Streak badge */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.3,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FireIcon size={28} active={streak.currentStreak > 0} />
                <Typography sx={{
                  color: streak.currentStreak > 0 ? GOLD : c.textMuted,
                  fontSize: '1.8rem',
                  fontWeight: 900,
                  lineHeight: 1,
                }}>
                  {streak.currentStreak || 0}
                </Typography>
              </Box>
              <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                day streak
              </Typography>
              {streak.bestStreak > 0 && streak.bestStreak > streak.currentStreak && (
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', opacity: 0.6 }}>
                  best: {streak.bestStreak}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Progress bar */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
              <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600 }}>
                {completedCount}/{totalDrills} completed
              </Typography>
              {allComplete && (
                <Typography sx={{ color: BLUE, fontSize: '1.05rem', fontWeight: 700 }}>
                  All done!
                </Typography>
              )}
            </Box>
            <Box sx={{
              width: '100%',
              height: 8,
              borderRadius: 4,
              backgroundColor: `${c.textMuted}15`,
              overflow: 'hidden',
            }}>
              <Box
                component={motion.div}
                initial={{ width: 0 }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  background: allComplete
                    ? `linear-gradient(90deg, ${BLUE}, ${BLUE})`
                    : `linear-gradient(90deg, ${ACCENT}, ${BLUE})`,
                }}
              />
            </Box>
          </Box>

          {/* Drill cards */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {drillList.map((drill, i) => {
              const isCompleted = drill.completed;
              const isLocked = drill.locked;
              const drillColor = {
                punishment: RED,
                opening: BLUE,
                checkmate: GOLD,
                blunder: ACCENT,
              }[drill.type] || ACCENT;

              return (
                <Box
                  key={drill.id}
                  component={motion.div}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.08 }}
                  onClick={() => {
                    if (isLocked || isCompleted) return;
                    // Navigate to appropriate training mode
                    const modeMap = {
                      punishment: 'punishment',
                      opening: 'openings',
                      checkmate: 'checkmate',
                      blunder: 'blunders',
                    };
                    const mode = modeMap[drill.type] || '';
                    navigate(`/dashboard/training?username=${encodeURIComponent(username)}${mode ? `&mode=${mode}` : ''}`);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: '14px',
                    backgroundColor: isCompleted ? `${BLUE}08` : isLocked ? `${c.textMuted}05` : `${drillColor}08`,
                    border: `1px solid ${isCompleted ? BLUE + '25' : isLocked ? c.textMuted + '10' : drillColor + '20'}`,
                    cursor: isLocked || isCompleted ? 'default' : 'pointer',
                    opacity: isLocked ? 0.5 : 1,
                    transition: 'all 0.15s ease',
                    '&:hover': isLocked || isCompleted ? {} : {
                      backgroundColor: `${drillColor}14`,
                      border: `1px solid ${drillColor}40`,
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  {/* Status circle */}
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isCompleted ? `${BLUE}20` : `${drillColor}15`,
                    color: isCompleted ? BLUE : drillColor,
                    flexShrink: 0,
                  }}>
                    {isCompleted ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : isLocked ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0110 0v4" />
                      </svg>
                    ) : (
                      <DrillIcon type={drill.type} size={20} />
                    )}
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      color: isCompleted ? BLUE : c.textDark,
                      fontSize: '1.05rem',
                      fontWeight: 700,
                      textDecoration: isCompleted ? 'line-through' : 'none',
                      opacity: isCompleted ? 0.7 : 1,
                    }}>
                      {drill.title}
                    </Typography>
                    <Typography sx={{
                      color: c.textMuted,
                      fontSize: '1.05rem',
                      mt: 0.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {drill.description}
                    </Typography>
                  </Box>

                  {/* Arrow or checkmark */}
                  {!isLocked && !isCompleted && (
                    <Box sx={{ color: drillColor, opacity: 0.5, flexShrink: 0 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Streak week dots */}
          <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${c.textMuted}10` }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1.5, md: 2.5 } }}>
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => {
                const today = new Date();
                const currentDay = (today.getDay() + 6) % 7; // Monday = 0
                const isPast = i < currentDay;
                const isToday = i === currentDay;
                const isFuture = i > currentDay;
                // Simplified: show completed for past days if streak covers them
                const isActive = isPast && streak.currentStreak > (currentDay - i);

                return (
                  <Box key={day} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isActive ? `${BLUE}20` : isToday ? `${ACCENT}15` : 'transparent',
                      border: isToday ? `2px solid ${ACCENT}` : isActive ? `2px solid ${BLUE}50` : `1px solid ${c.textMuted}20`,
                    }}>
                      {isActive ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : isToday && allComplete ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <Box sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: isFuture ? `${c.textMuted}20` : isToday ? ACCENT : `${c.textMuted}30`,
                        }} />
                      )}
                    </Box>
                    <Typography sx={{
                      color: isToday ? c.textDark : c.textMuted,
                      fontSize: '1.05rem',
                      fontWeight: isToday ? 700 : 500,
                    }}>
                      {day}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* ══════════════════════════════════════════════════════
            WEEKLY PROGRESS PULSE
        ══════════════════════════════════════════════════════ */}
        {weeklyProgress?.available && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            sx={{
              ...getCardSx(c),
              p: { xs: 2.5, md: 3.5 },
              mb: 4,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
              <Box>
                <Typography sx={{ color: c.textDark, fontSize: { xs: '1.3rem', md: '1.6rem' }, fontWeight: 800, letterSpacing: '-0.02em' }}>
                  Weekly Progress
                </Typography>
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mt: 0.3 }}>
                  Week ending {new Date(weeklyProgress.weekEnding + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                </Typography>
              </Box>
              <Box sx={{ color: BLUE, opacity: 0.6 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
              </Box>
            </Box>

            {/* Game recap stats */}
            {weeklyProgress.gameRecap && (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                gap: 2,
                mb: 2.5,
              }}>
                {[
                  { label: 'Games', value: weeklyProgress.gameRecap.gamesPlayed, color: c.textDark },
                  { label: 'Wins', value: weeklyProgress.gameRecap.wins, color: BLUE },
                  { label: 'Losses', value: weeklyProgress.gameRecap.losses, color: RED },
                  { label: 'Moves', value: weeklyProgress.gameRecap.totalMoves, color: BLUE },
                ].map((stat) => (
                  <Box key={stat.label} sx={{
                    textAlign: 'center',
                    p: 1.5,
                    borderRadius: '12px',
                    backgroundColor: `${stat.color}08`,
                  }}>
                    <Typography sx={{ color: stat.color, fontSize: '1.6rem', fontWeight: 900, lineHeight: 1 }}>
                      <AnimNum value={stat.value || 0} />
                    </Typography>
                    <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, mt: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Insights */}
            {weeklyProgress.insights?.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, mb: weeklyProgress.archetypeEvolution ? 2 : 0 }}>
                {weeklyProgress.insights.map((insight, i) => (
                  <Box key={i} sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: '10px',
                    backgroundColor: insight.positive ? `${BLUE}06` : `${RED}06`,
                    border: `1px solid ${insight.positive ? BLUE + '15' : RED + '15'}`,
                  }}>
                    <Box sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: insight.positive ? `${BLUE}15` : `${RED}15`,
                      color: insight.positive ? BLUE : RED,
                      flexShrink: 0,
                    }}>
                      {insight.positive ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                          <polyline points="17 6 23 6 23 12" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
                          <polyline points="17 18 23 18 23 12" />
                        </svg>
                      )}
                    </Box>
                    <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 500 }}>
                      {insight.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Archetype evolution */}
            {weeklyProgress.archetypeEvolution && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: '10px',
                backgroundColor: `${ACCENT}08`,
                border: `1px solid ${ACCENT}15`,
                mt: weeklyProgress.insights?.length > 0 ? 1.2 : 0,
              }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: `${ACCENT}15`,
                  color: ACCENT,
                  flexShrink: 0,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2L2 7l10 5 10-5-10-5z" />
                    <path d="M2 17l10 5 10-5" />
                    <path d="M2 12l10 5 10-5" />
                  </svg>
                </Box>
                <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 500 }}>
                  {weeklyProgress.archetypeEvolution.text}
                </Typography>
              </Box>
            )}

            {/* Top opening this week */}
            {weeklyProgress.topOpening && (
              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${c.textMuted}10`, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>
                  Most played:
                </Typography>
                <Typography sx={{ color: BLUE, fontSize: '1.05rem', fontWeight: 600 }}>
                  {weeklyProgress.topOpening.name} ({weeklyProgress.topOpening.games} games)
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Explore tiles are now part of the strength/weakness grid above */}

      </Box>
    </Box>
  );
}